import { useCallback, useEffect, useRef, useState } from 'react'
import { useOidc, useOidcAccessToken } from '@axa-fr/react-oidc'
import { useLocation } from 'react-router-dom'
import { defineMessages, useIntl } from 'react-intl'
import ConfirmationDialog from '../Dialog/ConfirmationDialog'
import { LOGGING_OUT } from '../mike-login'
import {
  setSessionExpiredHandler,
  clearSessionEnded,
  resetSessionEnded,
  isSigningOut,
  SessionEndedReason
} from '../../actions/sessionBridge'

const localMessages = defineMessages({
  expiredTitle: {
    id: 'screens.sessionExpired.title'
  },
  expiredMessage: {
    id: 'screens.sessionExpired.message'
  },
  signedOutTitle: {
    id: 'screens.sessionExpired.signedOutTitle'
  },
  signedOutMessage: {
    id: 'screens.sessionExpired.signedOutMessage'
  },
  signIn: {
    id: 'screens.sessionExpired.signIn'
  }
})

const SessionExpiredDialog = () => {
  const [reason, setReason] = useState<SessionEndedReason | null>(null)
  const { login, isAuthenticated } = useOidc()
  const { accessToken } = useOidcAccessToken()
  const intl = useIntl()
  const { pathname, search } = useLocation()

  const hasBeenAuthenticated = useRef(false)
  if (isAuthenticated) hasBeenAuthenticated.current = true

  // A token we have not seen before means the session recovered — whether the
  // user signed in again or a silent renew landed behind the dialog.
  const seenToken = useRef(accessToken)

  useEffect(() => {
    if (!accessToken || accessToken === seenToken.current) return
    seenToken.current = accessToken
    setReason(null)
    clearSessionEnded()
  }, [accessToken])

  useEffect(() => {
    setSessionExpiredHandler(nextReason => {
      const signingOutDeliberately =
        sessionStorage.getItem(LOGGING_OUT) === 'true' || isSigningOut()

      if (signingOutDeliberately || !hasBeenAuthenticated.current) {
        resetSessionEnded()
        return
      }

      setReason(nextReason)
    })
    return () => setSessionExpiredHandler(null)
  }, [])

  const handleSignIn = useCallback(() => {
    setReason(null)
    clearSessionEnded()
    login(search.length > 1 ? pathname + search : pathname)
  }, [login, pathname, search])

  // Keep the copy stable while MUI fades the dialog out: `reason` is already
  // null by then, and either comparison against it flips the text mid-exit.
  const shownReason = useRef<SessionEndedReason>('expired')
  if (reason) shownReason.current = reason

  const expired = shownReason.current === 'expired'

  return (
    <ConfirmationDialog
      open={reason !== null}
      title={intl.formatMessage(
        expired ? localMessages.expiredTitle : localMessages.signedOutTitle
      )}
      message={intl.formatMessage(
        expired ? localMessages.expiredMessage : localMessages.signedOutMessage
      )}
      ok={intl.formatMessage(localMessages.signIn)}
      onOk={handleSignIn}
      hideCancelButton
      disableDismiss
    />
  )
}

export default SessionExpiredDialog
